import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  countProperties,
  createProperty,
  generateDescriptionApi,
  getProperties,
  getPropertiesByOwnwerId,
  getPropertyById,
} from "../api/listing/listingApi";
import { backendFeatures } from "../config/backendFeatures";
import { retryTransient } from "../helpers/httpError";
import { GetPropertiesFilters } from "../types/listings/listing.model";
import { useListingStore } from "../store/useListingStore";
import { useUploadImagesStore } from "../store/useUploadImagesStore";

export const useCreateProperty = () => {
  const setImages = useUploadImagesStore((s) => s.setImages);
  return useMutation({
    mutationFn: createProperty,
    onSuccess: () => {
      setImages([]);
    },
  });
};

// Results of recent list requests, by their exact filters. Going back from a
// listing to the list (or home) shows the same results at once — no blank
// list, no jump — while a fresh copy loads quietly in the background.
const LIST_CACHE_TTL_MS = 10 * 60 * 1000;
const listCache = new Map<string, { data: any; at: number }>();

export const useGetPropertiesWithFilters = () => {
  const setProperties = useListingStore((s) => s.setListingData);
  // Filter changes (region/category/rooms pills, quick re-clicks) can fire a
  // new request before the previous one resolves. Network order isn't
  // guaranteed to match request order, so an older, slower response can
  // arrive last and overwrite the store with stale results (e.g. selecting
  // "1-комнатная" briefly shows whatever the previous, unfiltered request
  // returns). Tag each call with an incrementing id and drop any response
  // that isn't from the most recently issued request.
  const latestRequestId = useRef(0);
  return useMutation({
    mutationFn: async (filters: GetPropertiesFilters) => {
      const requestId = ++latestRequestId.current;
      const key = JSON.stringify(filters);
      const cached = listCache.get(key);
      if (cached && Date.now() - cached.at < LIST_CACHE_TTL_MS) {
        // Stale-while-revalidate: show the cached page right away.
        setProperties(cached.data);
      }
      const data = await getProperties(filters);
      listCache.set(key, { data, at: Date.now() });
      if (listCache.size > 50) listCache.delete(listCache.keys().next().value as string);
      return { data, requestId };
    },
    onSuccess: ({ data, requestId }) => {
      if (requestId !== latestRequestId.current) return;
      setProperties(data);
      return data;
    },
  });
};

export const useGetById = () => {
  const setProperty = useListingStore((s) => s.setProperty);
  // Clicking A → B quickly: A's late answer must not overwrite B in the store.
  const latest = useRef<string>("");
  return useMutation({
    mutationFn: (id: string) => {
      latest.current = id;
      return getPropertyById(id);
    },
    // A network blip / 5xx is retried; only a real 404 ends on «не найдено».
    retry: retryTransient,
    onSuccess: (data, id) => {
      if (id !== latest.current) return data;
      setProperty({
        ...data,
        createdAt: data.createdat,
        isTop: data.is_top,
        isVip: data.is_vip,
        isUrgent: data.is_urgent,
        seller: {
          phone: data.phone_number,
          email: data.email,
        },
        views: data.views,
      });
      return data;
    },
  });
};


export const useGetPropertiesByOwnerId = (ownerId: number) => {
  return useQuery({
    // ownerId MUST be part of the key — otherwise every profile page shares
    // one cache entry and navigating between owners shows stale (or empty)
    // listings until a manual refetch happens to win the race.
    queryKey: ["propertiesByOwner", ownerId],
    queryFn: () => getPropertiesByOwnwerId(ownerId),
    enabled: !!ownerId,
  });
};

export const useGenerateDescription = () => {
  return useMutation({
    mutationFn: generateDescriptionApi
  });
}

/**
 * Count of properties per filter value (homepage category cards, «real
 * estate by city» cards) via GET /api/property/count — it doesn't add
 * impressions to listings, unlike asking the list endpoint for one row.
 * Region counts still go through getProperties while Bishkek is split over
 * two region values (backend task B1, legacyCompat.ts).
 */
function usePropertyCountsBy(
  filterKey: "type" | "region",
  values: string[],
) {
  const queries = useQueries({
    queries: values.map((value) => ({
      queryKey: ["propertyCount", filterKey, value],
      queryFn: (): Promise<number> =>
        filterKey === "type" || backendFeatures.bishkekMigrated
          ? countProperties({ [filterKey]: value })
          : getProperties({ [filterKey]: value, page: 1, offset: 1 }).then((d: any) => Number(d?.total ?? 0)),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const counts: Record<string, number> = {};
  let isLoading = false;
  values.forEach((value, i) => {
    const q = queries[i];
    if (q.isLoading) isLoading = true;
    if (typeof q.data === "number") counts[value] = q.data;
  });

  return { counts, isLoading };
}

/** Object count per property type (APARTMENT / HOUSE / PLOT / COMMERCIAL, …)
 *  — the API filters by `type`, not `category` (see getProperties call
 *  sites), so that's the field used here. Powers the homepage category cards. */
export const useGetCategoryCounts = (types: string[]) =>
  usePropertyCountsBy("type", types);

/** Object count per region (BISHKEK, CHUI, …) — powers the "real estate by
 *  city" cards on the homepage. */
export const useGetRegionCounts = (regions: string[]) =>
  usePropertyCountsBy("region", regions);